import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail, resolveAvatarUrl, uploadImage } from '../api';
import { useAuth } from '../context/AuthContext';
import SocialsEditor from '../components/SocialsEditor';

/**
 * Profile page — display_name · handle · bio · categories · avatar.
 * Split out from AccountSettings on 2026-07-15 per user feedback: Account Settings should
 * only contain account/auth items (email, password, 2FA). Profile has its own route.
 */

const CATEGORY_FALLBACK = [
  { key: 'Trader', color: 'c-orange' },
  { key: 'Content Creator', color: 'c-pink' },
  { key: 'Coach', color: 'c-blue' },
  { key: 'Educator', color: 'c-green' },
  { key: 'Community Builder', color: 'c-purple' },
  { key: 'Influencer', color: 'c-yellow' },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshMe } = useAuth();

  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(searchParams.get('edit') === '1');
  const socialsRef = useRef(null);
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [CATEGORIES, setCategoryList] = useState(CATEGORY_FALLBACK);

  useEffect(() => {
    api.get('/categories')
      .then(({ data }) => {
        if (Array.isArray(data.categories) && data.categories.length) {
          setCategoryList(data.categories.map(c => ({ key: c.key, color: c.color, label: c.label })));
        }
      })
      .catch(() => {});
  }, []);
  const [bio, setBio] = useState(user?.bio || '');
  const [categories, setCategories] = useState(user?.categories || []);
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const [socials, setSocials] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/socials').then(({ data }) => setSocials(data.socials || {})).catch(() => {});
  }, []);

  // If arrived with ?focus=socials, scroll the Socials editor into view once edit mode is on.
  useEffect(() => {
    if (!editing) return;
    if (searchParams.get('focus') !== 'socials') return;
    const t = setTimeout(() => {
      socialsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(t);
  }, [editing, searchParams]);

  useEffect(() => {
    if (user && typeof user === 'object') {
      setDisplayName(user.display_name || '');
      setBio(user.bio || '');
      setCategories(user.categories || []);
      setAvatar(user.avatar || null);
    }
  }, [user]);

  const initials = (user?.display_name || user?.handle || 'U')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const toggleCategory = (k) => {
    setCategories((prev) => {
      if (prev.includes(k)) return prev.filter((x) => x !== k);
      if (prev.length >= 3) return prev;
      return [...prev, k];
    });
  };

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setErr('Please choose an image under 4 MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErr('Please choose an image file.');
      return;
    }
    setErr('');
    setSaving(true);
    try {
      const { path } = await uploadImage(file);
      setAvatar(path); // storage path e.g. "clanster/uploads/<uid>/<uuid>.jpg"
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Upload failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    displayName.trim().length >= 1 &&
    bio.length <= 160 &&
    !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setErr('');
    try {
      await api.put('/profile', {
        display_name: displayName.trim(),
        bio,
        categories,
        avatar,
      });
      await refreshMe();
      setEditing(false);
      setToast({ msg: '✓ Profile updated' });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDisplayName(user?.display_name || '');
    setBio(user?.bio || '');
    setCategories(user?.categories || []);
    setAvatar(user?.avatar || null);
    setErr('');
    setEditing(false);
  };

  return (
    <AppShell activeTab="wallet">
      <div className="settings-wrap" data-testid="profile-page">
        <h1 className="settings-title">Your profile</h1>

        <div className="settings-card profile-card-block">
          {!editing ? (
            <div className="profile-card-view" data-testid="profile-view">
              <div className="settings-avatar" aria-hidden="true">
                {resolveAvatarUrl(avatar) ? <img src={resolveAvatarUrl(avatar)} alt="" /> : <span>{initials}</span>}
              </div>
              <div className="profile-info">
                <div className="profile-name" data-testid="profile-display-name">{user?.display_name}</div>
                <div className="profile-handle" data-testid="profile-handle">@{user?.handle}</div>
                {user?.bio && <div className="profile-bio">{user.bio}</div>}
                {user?.categories?.length > 0 && (
                  <div className="profile-cats">
                    {user.categories.map((c) => <span key={c} className="cat-pill">{c}</span>)}
                  </div>
                )}
              </div>
              <button className="btn-ghost" onClick={() => setEditing(true)} data-testid="profile-edit-btn">
                Edit
              </button>
            </div>
          ) : (
            <div className="profile-card-edit" data-testid="profile-edit-form">
              <div className="avatar-row" style={{ marginBottom: 18 }}>
                <label className="avatar-upload">
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarChange} data-testid="profile-avatar-input" />
                  <div className="avatar-ring">
                    <div className="avatar-inner">
                      {resolveAvatarUrl(avatar) ? <img src={resolveAvatarUrl(avatar)} alt="Your avatar" /> : <span style={{ fontWeight: 700 }}>{initials}</span>}
                    </div>
                  </div>
                  <div className="avatar-cam">
                    <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                      <path d="M4 8h3l2-2h6l2 2h3v11H4V8z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
                      <circle cx="12" cy="13" r="3" stroke="#fff" strokeWidth="1.8" />
                    </svg>
                  </div>
                </label>
                <p className="avatar-copy"><b>Update photo</b><br />JPG, PNG or WEBP, under 4 MB.</p>
              </div>

              <div className="field">
                <div className="field-label"><span>Display name</span></div>
                <input
                  className="text-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 40))}
                  data-testid="profile-name-input"
                />
              </div>

              <div className="field">
                <div className="field-label">
                  <span>Username</span>
                </div>
                <div className="input-wrap">
                  <span className="input-prefix">@</span>
                  <input
                    className="text-input with-prefix"
                    value={(user?.handle || '').replace(/^@/, '')}
                    disabled
                    readOnly
                    aria-readonly="true"
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    data-testid="profile-handle-input"
                  />
                </div>
                <div className="validation-row" data-testid="profile-handle-locked-note" style={{ color: 'var(--muted, #8a8397)', fontSize: 12 }}>
                  <span>Your username is permanent and can't be changed.</span>
                </div>
              </div>

              <div className="field">
                <div className="field-label">
                  <span>Short bio</span>
                  <span className="char-count">{bio.length} / 160</span>
                </div>
                <textarea
                  className="text-input"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 160))}
                  data-testid="profile-bio-input"
                />
              </div>

              <span className="chip-field-label">Categories <span className="light">(pick up to 3)</span></span>
              <div className="chip-row" data-testid="profile-category-chips">
                {CATEGORIES.map((c) => (
                  <div
                    key={c.key}
                    className={`chip ${c.color}${categories.includes(c.key) ? ' selected' : ''}`}
                    onClick={() => toggleCategory(c.key)}
                    role="button"
                    tabIndex={0}
                    data-testid={`profile-cat-${c.key.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {c.label || c.key}
                  </div>
                ))}
              </div>

              {err && (
                <p style={{ color: 'var(--error)', fontSize: 13, margin: '8px 0', fontWeight: 700 }} data-testid="profile-err">
                  {err}
                </p>
              )}

              <div style={{ marginTop: 20 }} ref={socialsRef} data-testid="profile-socials-section">
                <label className="settings-field-label" style={{ marginBottom: 8, display: 'block' }}>
                  Social links
                </label>
                <SocialsEditor
                  value={socials}
                  onChange={setSocials}
                  onError={(msg) => msg && setErr(msg)}
                />
              </div>

              <div className="settings-action-row">
                <button className="btn-secondary" onClick={cancel} data-testid="profile-cancel-btn">Cancel</button>
                <button
                  className="btn-primary"
                  disabled={!canSave}
                  onClick={save}
                  data-testid="profile-save-btn"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          className="btn-ghost"
          style={{ marginTop: 4 }}
          onClick={() => navigate(-1)}
          data-testid="profile-back-btn"
        >
          ← Back
        </button>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
